import { parseISO } from "date-fns";
import { formatDate } from "./CommonFunctions";

const options = { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' };

/**
 * Builds a pdfmake document definition for a single check-in form.
 * Used by both CheckinForm.jsx (individual) and BatchCheckinPrintDialog.jsx (batch).
 *
 * @param {object} tenancyDetails - tenancy fields (roomid, checkin, checkout, rate, etc.)
 * @param {Array}  customersDetails - array of { fname, lname, phone, email }
 * @returns {object} pdfmake documentDefinition
 */
export function buildCheckinDocDefinition(tenancyDetails, customersDetails) {
  const today = parseISO(formatDate(new Date())).toLocaleDateString('en-us', options);

  // ── Name string ──────────────────────────────────────────────
  const nameString = customersDetails
    .map((c, i) => (i === 0 ? `${c.fname} ${c.lname}` : `, ${c.fname} ${c.lname}`))
    .join('');

  // ── Terms ────────────────────────────────────────────────────
  const terms = [
    'This is an extended stay motel and stay is temporary. This is not your primary residence. You are a transient occupant.\n',
    'You can not use the motel address as your personal or business address.\n',
    'Check-out time is at 11:00 A.M.\n',
    'If you would like to occupy the premises for longer, you must speak with someone at the front desk and come to a written agreement. If no agreement is reached, you must vacate by the check-out time and date.\n',
    'Guests will be held responsible for any loss or damage done to a room during their stay.\n',
    'Motel Management is not responsible for your personal belongings or valuables.\n',
    'We check toilets for clogging issues before your stay. If the toilet is clogged during your stay, a sum of $250 per plumber call will be charged. Guest will be responsible for any additional charges if applicable\n',
    'The room you are assigned MUST be left in the same condition in which you rented it.\n',
    'No tampering with or removal of smoke detectors.\n',
    'Speed Limit 5 MPH. Careful driving is expected \n',
  ];
  const termsContent = terms.map(t => ({ text: t, style: 'listItem' }));

  // ── Rules ────────────────────────────────────────────────────
  const rules = [
    'No Pets. If any pets are found, the tenant agrees to pay a fine of $500 and move out immediately. If damages are more than $500, the tenant agrees to pay the attorney and legal fees.',
    'No smoking allowed in the rooms. You will be asked to leave immediately and legal action will follow if we find you smoking inside the room. Quiet hours are from 9pm - 6am',
    'Cleanliness : We appreciate you keeping the rooms clean. We only allow clean tenants. You will be requested to leave if you keep the room dirty. Management reserves the right to decide.',
    'No loud music',
    'No loud arguments. Our guests like our place quiet. You will be asked to leave if you argue loud',
    'No additional guests / human trafficking / drugs. If we find traffic to the room, you will be asked to leave immediately. We work with police to stop human trafficking and drugs. We alert the police immediately and you will be reported as a potential drug dealer or human trafficker. You agree to leave if any such incidents happen',
    'Zero tolerance for slamming the doors. You will be asked to leave immediately and legal action may follow if there are damages',
    'Courtesy: Please be courteous to the employees. We can discuss and resolve problems without raising the voice.',
    'Inspections. Management will do inspections for smoking, cleanliness, presence of pets or any safety or operational concerns.',
  ];
  const rulesContent = rules.map(r => ({ text: r, style: 'listItem' }));

  // ── Document definition ──────────────────────────────────────
  return {
    content: [
      { text: 'Tidy Inn LLC\n1601 George Jenkins Blvd\nLakeland Fl. 3381\n863-825-5007 \n', style: 'header' },
      { text: '', style: 'subheader' },
      {
        margin: [20, 0, 0, 10], fontSize: 11,
        text: [{ text: 'Name(s) : ', style: 'inputLabel' }, { text: nameString + '\n', bold: true }],
      },
      {
        alignment: 'justify', margin: [20, 0, 0, 10], fontSize: 11,
        columns: [
          { text: [{ text: ' Phone Number : ', style: 'inputLabel' }, { text: customersDetails[0].phone + '\n', bold: true }] },
          { text: [{ text: ' Email Address : ', style: 'inputLabel' }, { text: (customersDetails[0].email || '') + '\n', bold: true }] },
        ],
      },
      {
        alignment: 'justify', margin: [20, 0, 0, 10], fontSize: 11, lineHeight: 1.7,
        columns: [
          {
            text: [
              { text: 'Checkin Date : ', style: 'inputLabel' },
              { text: parseISO(tenancyDetails.checkin).toLocaleDateString('en-us', options) + '\n', bold: true },
              { text: 'Rate : $', style: 'inputLabel' },
              { text: tenancyDetails.rate + ' (' + tenancyDetails.rentalType + ')' + '\n', bold: true },
              { text: 'Number Of Adults # : ', style: 'inputLabel' },
              { text: tenancyDetails.adults + '\n', bold: true },
              { text: 'Expected Checkout Date : ', style: 'inputLabel' },
              { text: parseISO(tenancyDetails.checkout).toLocaleDateString('en-us', options) + '\n', bold: true },
            ],
          },
          {
            text: [
              { text: 'Room # : ', style: 'inputLabel' },
              { text: tenancyDetails.roomid + '\n', bold: true },
              { text: 'Room Deposit : $', style: 'inputLabel' },
              { text: tenancyDetails.deposit + '\n', bold: true },
              { text: 'Number Of Children # : ', style: 'inputLabel' },
              { text: tenancyDetails.children + '\n\n', bold: true },
            ],
          },
        ],
      },
      { text: 'Terms and Conditions \n', style: 'header' },
      { ol: termsContent, style: ' listItems' },
      { text: 'By signing you acknowledge you have read all of the above terms and conditions\n\n', style: 'statement' },
      { text: 'Guest Signature............................................\t\t\tManager Signature......................................\n\n', style: 'statement' },
      { text: 'Date: ' + today + '\n\n\n\n\n\n\n', style: 'statement' },
      { text: '\nTIDY INN HOUSE RULES \n\n', style: 'header' },
      { ul: rulesContent, style: 'listItems' },
      { text: '\nI agree with the house rules. I agree to check out immediately if management decides that rules are broken. I will be responsible for any damages / court charges caused\n\n', style: 'statement' },
      { text: 'Guest Signature........................................\t\t\tManager Signature......................................\n\n', style: 'statement' },
      { text: 'Date: ' + today + '\n\n\n\n', style: 'statement' },
      { text: '\n\n\n\n\n\n\n\n\n\nTidy Inn LLC\n1601 George Jenkins Blvd\nLakeland Fl. 3381\n863-825-5007 \n', style: 'header' },
      { text: 'Room Number #: ' + tenancyDetails.roomid + '\n\n' },
      {
        margin: [20, 0, 0, 10], fontSize: 11,
        text: [{ text: 'Name(s) : ' }, { text: nameString + '\n\n', bold: true }],
      },
      { bold: false, text: 'I understand and agree that this will not be my primary residence. I have a permanent address somewhere else and agree and understand that this will be a transient occupancy.\n\n', style: 'statement' },
      { bold: false, text: 'I agree to check out on ' + tenancyDetails.checkout + ' before 11:00 am EST. I will remove all my stuff, leave the room in a clean condition, and return the keys to the manager at the front desk as well.', style: 'statement' },
      { text: '\n\nGuest Signature.............................................\t\t\tManager Signature.......................................\n\n', style: 'statement' },
      { text: 'Date: ' + today + '\n\n', style: 'statement' },
    ],
    styles: {
      header: { fontSize: 12, bold: true, alignment: 'center', margin: [0, 0, 0, 20] },
      subheader: { fontSize: 11, bold: true, margin: [0, 0, 0, 10] },
      listItem: { fontSize: 10, margin: [10, 0, 10, 7], alignment: 'left', lineHeight: 1.5, bold: false },
      statement: { fontSize: 10, margin: [0, 0, 10, 7], alignment: 'left', lineHeight: 1.5, bold: true },
      inputLabel: { fontSize: 10, bold: true, margin: [0, 0, 0, 20], color: 'grey' },
    },
  };
}